import { Inject, Injectable } from "@angular/core";
import { SelectOption } from "@nms-angular-toolkit/nms-select";
import { GenerateAlphabeticIdentifierService } from "@nms-ng2/app/modules/topology/utils/generate-alphabetic-identifier.service";
import { ColumnConfigType, SimpleTableColumnConfig } from "@nms-angular-toolkit/nms-simple-table";
import { ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { PRIMARY_PRESSED_COLOR } from "@nms-ng2/app/shared/constants/colors";
import { NmsDialogService } from "@nms-angular-toolkit/nms-dialog";
import { OscInputData, OscManagementData, OscManagementPortData, OscManagementStepModel } from "../osc-management-step.model";
import { LinkInfoCircuit, LinksStepModel } from "../../4-links/links-step.model";
import { EndsStepModel } from "../../1-ends/ends-step.model";
import { IntermediatesStepModel } from "../../3-intermediates/intermediates-step.model";
import { NmsWizardStepMapData } from "@nms-angular-toolkit/nms-wizard";

/**
 * Serviço responsável tratar os dados de gerência OSC que são apresentados na tabela.
 */
@Injectable({
    providedIn: "root"
})
export class OscManagementDataService {

    constructor(private readonly generateIdentifierService: GenerateAlphabeticIdentifierService,
            @Inject(ANGULARJS_TRANSLATE) private readonly translate: any, private readonly nmsDialogService: NmsDialogService) {
        // Construtor intencionalmente vazio
    }

    initializeTableColumns(isToggleDisabledCallback: Function, emitChangesWereMade: Function,
            currentStepsData: NmsWizardStepMapData): SimpleTableColumnConfig[] {
        return [
            {
                field: "identifier",
                label: this.translate.instant("dwdm.circuit.wizard.osc.management.step.table.columns.identifier"),
                width: "15%",
                customColor: PRIMARY_PRESSED_COLOR,
            },
            {
                field: "endName",
                width: "30%",
                label: this.translate.instant("dwdm.circuit.wizard.osc.management.step.table.columns.device"),
            },
            {
                field: "port",
                width: "15%",
                label: this.translate.instant("dwdm.circuit.wizard.osc.management.step.table.columns.oscPort"),
                optionalColumn: {
                    type: ColumnConfigType.SELECT,
                    selectOptions: (row: OscManagementData) => row.portsSelection,
                    required: true,
                    multiple: false,
                    placeholder: this.translate.instant("dwdm.circuit.wizard.ends.step.select"),
                    selectionAction: () => {
                        emitChangesWereMade(currentStepsData);
                    }
                },
            },
            {
                field: "linkTxName",
                width: "15%",
                label: this.translate.instant("dwdm.circuit.wizard.osc.management.step.table.columns.linkTx"),
            },
            {
                field: "linkRxName",
                width: "15%",
                label: this.translate.instant("dwdm.circuit.wizard.osc.management.step.table.columns.linkRx"),
            },
            {
                field: "actions",
                label: this.translate.instant("dwdm.circuit.wizard.osc.management.step.table.columns.actions"),
                width: "10%",
                optionalColumn: {
                    type: ColumnConfigType.ACTIONS,
                    actions: [
                        {
                            id: "toggle",
                            variant: "toggle",
                            isDisabled: isToggleDisabledCallback.call(null),
                            onAction: (row: OscManagementData) => {
                                this.onRowToggleChange(row, emitChangesWereMade, currentStepsData);
                            },
                            getTooltip: () => {
                                return this.translate.instant("dwdm.circuit.wizard.osc.management.row.toggle.tooltip");
                            }
                        }
                    ]
                },
            }
        ];
    }

    processOscManagementData(oscManagementData: OscManagementData[], links: LinkInfoCircuit[],
            identifiersInUse: Array<string>): OscManagementData[] {
        const oscPortList = new Array();
        oscManagementData.forEach((oscManagement: OscManagementData) => {
            oscManagement.portsSelection = this.convertPortsToSelectOptions(oscManagement.ports);
            oscManagement.linkTxName = links.find(link => link.id === oscManagement.txLinkId).identifier;
            oscManagement.linkRxName = links.find(link => link.id === oscManagement.rxLinkId).identifier;
            oscManagement.toggleActive = oscManagement.isUsed;
            oscManagement.identifier = this.generateIdentifierService.generate("OSC", identifiersInUse);

            oscPortList.push(oscManagement);
        })

        return oscPortList;
    }

    buildOscInputData(endsStepModel: EndsStepModel, intermediatesStepModel: IntermediatesStepModel,
                linksStepModel: LinksStepModel) {
        const aEndId = endsStepModel.aEndDevice.id;
        const zEndId = endsStepModel.zEndDevice.id;
        const intermediatesResourceIds = intermediatesStepModel.devices.map(device => device.resourceId);
        const linksIds = linksStepModel.links.map(link => link.id);

        return new OscInputData(aEndId, zEndId, intermediatesResourceIds, linksIds);
    }

    fillTableDataFromCache(tableData: OscManagementData[], oscModel: OscManagementStepModel): OscManagementData[] {
        return this.fillTableFromCache(tableData, oscModel);
    }

    private fillTableFromCache(tableData: OscManagementData[], oscModel: OscManagementStepModel) {
        tableData.forEach((data: OscManagementData) => {
            const oscData = this.findOscData(oscModel, data);
            data.toggleActive = oscData?.toggleActive ?? false;
            data.port = oscData?.oscPort;
        });

        return tableData;
    }

    private findOscData(ocsStepModel: OscManagementStepModel, data: OscManagementData ) {
        return ocsStepModel.oscManagementPorts.find((osc) => osc.endId === data.endResourceId &&
            osc.linkRxId === data.rxLinkId && osc.linkTxId === data.txLinkId);
    }

    private convertPortsToSelectOptions(portsData: OscManagementPortData[]): SelectOption[] {
        return portsData.map((port: OscManagementPortData) => {
            return {
                id: port.circuitPortIdentifierData.portNo.toString(),
                label: port.portName
            }
        });

    }

    private onRowToggleChange(row: OscManagementData, emitChangesWereMade: Function,
            currentStepsData: NmsWizardStepMapData): void {
        if (row.toggleActive) {
            emitChangesWereMade(currentStepsData);
            return;
        }

        this.nmsDialogService.openDialog({
            title: this.translate.instant("modals.alert.action"),
            description: this.translate.instant("dwdm.circuit.wizard.osc.management.confirm.disable.osc.row"),
            isConfirm: true
        })
        .then(() => {
            row.port = undefined;
            emitChangesWereMade(currentStepsData);
        })
        .catch(() => {
            row.toggleActive = true;
            emitChangesWereMade(currentStepsData);
        });
    }
}
